import React, { useMemo } from 'react';
import { Typography, makeStyles } from '@material-ui/core';
import CustomRadioGroup from '../../shared/CustomRadioGroup';
import { getScreenPreviewGradient } from '../previewGradients';
import PreviewPhone from '../../../assets/PreviewPhone';
import RnpIcon from '../../../assets/RnpIcon';

const GRADIENT_OPTIONS = [
  { name: 'Gradient 1', value: '1' },
  { name: 'Gradient 2', value: '2' },
  { name: 'Gradient 3', value: '3' },
];

/* Screen inset matches PreviewPhone frame: translate(10.8151, 9.96582) 177.683×384.742 in 200×404 viewBox */
const SCREEN = {
  leftPct: (10.8151 / 200) * 100,
  topPct: (9.96582 / 404) * 100,
  widthPct: (177.683 / 200) * 100,
  heightPct: (384.742 / 404) * 100,
};

const useStyles = makeStyles(() => ({
  root: {
    marginTop: 8,
    marginBottom: 16,
    padding: '8px 0 0',
  },
  sectionTitle: {
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: 14,
    color: '#101828',
    marginBottom: 12,
  },
  gradientLabel: {
    fontFamily: 'Lato',
    fontWeight: 500,
    fontSize: 12,
    color: '#344054',
    marginBottom: 8,
  },
  radioRow: {
    marginBottom: 20,
  },
  phoneWrap: {
    position: 'relative',
    width: 200,
    maxWidth: '100%',
    margin: '0 auto',
  },
  phoneImg: {
    display: 'block',
    width: '100%',
    height: 'auto',
    verticalAlign: 'top',
  },
  screenInset: {
    position: 'absolute',
    left: `${SCREEN.leftPct}%`,
    top: `${SCREEN.topPct}%`,
    width: `${SCREEN.widthPct}%`,
    height: `${SCREEN.heightPct}%`,
    borderRadius: 28,
    overflow: 'hidden',
    zIndex: 1,
  },
  screenGradient: {
    position: 'absolute',
    inset: 0,
    opacity: 1,
  },
  logoLayer: {
    position: 'absolute',
    left: `${SCREEN.leftPct}%`,
    top: `${SCREEN.topPct}%`,
    width: `${SCREEN.widthPct}%`,
    height: `${SCREEN.heightPct}%`,
    borderRadius: 28,
    overflow: 'hidden',
    zIndex: 2,
    pointerEvents: 'none',
  },
  contentStack: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '88%',
    maxWidth: 168,
    textAlign: 'center',
  },
  previewIconImg: {
    width: 72,
    height: 72,
    objectFit: 'contain',
    display: 'block',
    flexShrink: 0,
  },
  rnpIconFallback: {
    width: 72,
    height: 72,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#fff',
    '& svg': {
      width: 48,
      height: 'auto',
    },
  },
  brandName: {
    marginTop: 12,
    fontFamily: 'Lato, sans-serif',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.25,
    color: '#fff',
    textShadow: '0 1px 6px rgba(0,0,0,0.45)',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const RnpScreenPreview = ({ backgroundColor, partnerName, iconSrc, gradientVariant, onGradientChange }) => {
  const classes = useStyles();
  const gradient = gradientVariant != null && gradientVariant !== '' ? String(gradientVariant) : '1';

  const screenBackground = useMemo(
    () => getScreenPreviewGradient(gradient, backgroundColor),
    [gradient, backgroundColor],
  );

  const handleGradientChange = (e) => {
    if (onGradientChange) onGradientChange(e.target.value);
  };

  const title = (partnerName && String(partnerName).trim()) || 'Partner name';

  return (
    <div className={classes.root}>
      <Typography component="h3" className={classes.sectionTitle}>
        Screen Preview
      </Typography>
      <Typography className={classes.gradientLabel}>Select a gradient :</Typography>
      <div className={classes.radioRow}>
        <CustomRadioGroup
          name="screenPreviewGradient"
          value={gradient}
          options={GRADIENT_OPTIONS}
          handleChange={handleGradientChange}
        />
      </div>
      <div className={classes.phoneWrap}>
        <PreviewPhone className={classes.phoneImg} />
        <div className={classes.screenInset}>
          <div className={classes.screenGradient} style={{ background: screenBackground }} />
        </div>
        <div className={classes.logoLayer}>
          <div className={classes.contentStack}>
            {iconSrc ? (
              <img src={iconSrc} alt="" className={classes.previewIconImg} />
            ) : (
              <span className={classes.rnpIconFallback} aria-hidden>
                <RnpIcon />
              </span>
            )}
            <div className={classes.brandName}>{title}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RnpScreenPreview;
